import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Toggle } from './ui/toggle';
import { Upload, X, Camera, Package } from 'lucide-react';
import { toast } from 'sonner';
import PricingSection from './PricingSection';
import HiveTypeConfig, { HIVE_TYPES, StackRow, normalizeBoxTypes } from './hive/HiveTypeConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;



const EditHiveDialog = ({ open, onClose, hive, yards, onUpdate, allHives }) => {
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isSplit, setIsSplit] = useState(false);
  const [splitType, setSplitType] = useState('');
  const [splitTypeOther, setSplitTypeOther] = useState('');
  const [hiveType, setHiveType] = useState('');
  const [hivePricing, setHivePricing] = useState({});
  const [defaultPrices, setDefaultPrices] = useState({});
  const [originPrice, setOriginPrice] = useState(0);
  const [adminComponents, setAdminComponents] = useState([]);
  const [adminBeeVarieties, setAdminBeeVarieties] = useState([]);
  const [adminOriginTypes, setAdminOriginTypes] = useState([]);
  const [adminHiveTypes, setAdminHiveTypes] = useState([]);
  const [userHiveTypes, setUserHiveTypes] = useState([]);
  const [selectedFrameTypes, setSelectedFrameTypes] = useState([]);
  const [selectedFoundationTypes, setSelectedFoundationTypes] = useState([]);
  const [frameQuantities, setFrameQuantities] = useState({});
  const [foundationQuantities, setFoundationQuantities] = useState({});
  const [allFramesSelected, setAllFramesSelected] = useState(false);
  const [allFoundationsSelected, setAllFoundationsSelected] = useState(false);
  const [suggestionName, setSuggestionName] = useState('');
  // Box configuration state
  const [boxConfig, setBoxConfig] = useState({});

  // Handle component suggestion
  const handleSuggestComponent = async () => {
    if (!suggestionName.trim()) return;
    try {
      const res = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: suggestionName.trim(),
          type: 'component',
          suggested_section: '',
          notes: ''
        })
      });
      if (res.ok) {
        toast.success('Suggestion submitted! Your admin will review it.');
        setSuggestionName('');
      } else {
        toast.error('Failed to submit suggestion');
      }
    } catch (error) {
      toast.error('Failed to submit suggestion');
    }
  };

  // Fetch admin-configured data (active items only)
  useEffect(() => {
    const fetchAdminConfig = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        const [componentsRes, varietiesRes, originsRes, hiveTypesRes, costsRes, userTypesRes] = await Promise.all([
          fetch(`${API}/config/admin_components`, { headers }),
          fetch(`${API}/config/bee_varieties`, { headers }),
          fetch(`${API}/config/origin_types`, { headers }),
          fetch(`${API}/config/hive_types`, { headers }),
          fetch(`${API}/hive-costs`, { headers }),
          fetch(`${API}/config/user-hive-types`, { headers }).catch(() => ({ ok: false }))
        ]);
        if (componentsRes.ok) {
          const data = await componentsRes.json();
          // Handle both array and {items: []} response formats
          setAdminComponents(Array.isArray(data) ? data : (data.items || []));
        }
        if (varietiesRes.ok) {
          const data = await varietiesRes.json();
          setAdminBeeVarieties(Array.isArray(data) ? data : (data.items || []));
        }
        if (hiveTypesRes.ok) {
          const data = await hiveTypesRes.json();
          setAdminHiveTypes(Array.isArray(data) ? data : (data.items || []));
        }
        if (userTypesRes?.ok) {
          const data = await userTypesRes.json();
          setUserHiveTypes(Array.isArray(data) ? data : []);
        }
        if (originsRes.ok) {
          const data = await originsRes.json();
          setAdminOriginTypes(Array.isArray(data) ? data : (data.items || []));
        }
        if (costsRes.ok) {
          const data = await costsRes.json();
          setDefaultPrices(data.prices || {});
        }
      } catch (error) {
      }
    };
    if (open) {
      fetchAdminConfig();
    }
  }, [open]);

  // Helper to render custom components for a specific section
  // Build dynamic component sections from admin_components
  const renderDynamicComponentSections = () => {
    const sections = {};
    const sectionLabels = {
      lids_covers: { label: 'Lids & Covers', icon: '🎩', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
      bottom_boards_stands: { label: 'Bottom Boards & Stands', icon: '📦', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
      feeders: { label: 'Feeders', icon: '🍯', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
      queen_bee_management: { label: 'Queen Management', icon: '👑', bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
      pest_control: { label: 'Pest Control', icon: '🛡️', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
      heavy_accessories: { label: 'Heavy & Accessories', icon: '⚙️', bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-800' },
      miscellaneous: { label: 'Miscellaneous', icon: '📋', bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-800' },
    };

    adminComponents.filter(c => c.is_active !== false).forEach(comp => {
      const section = comp.section || 'miscellaneous';
      if (!sections[section]) sections[section] = [];
      sections[section].push(comp);
    });

    return Object.entries(sections).map(([sectionKey, components]) => {
      const style = sectionLabels[sectionKey] || { 
        label: sectionKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), 
        icon: '✨', bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' 
      };
      return (
        <div key={sectionKey} className={`${style.bg} border ${style.border} rounded-xl p-3`}>
          <h4 className={`text-sm font-semibold ${style.text} mb-2`}>{style.icon} {style.label}</h4>
          <div className="grid grid-cols-2 gap-3">
            {components.map(comp => {
              const compKey = comp.form_key || comp.name.toLowerCase().replace(/\s+/g, '_');
              return (
                <div key={comp.id}>
                  <Label className="text-xs text-stone-600">{comp.name}</Label>
                  <Input type="number" min="0"
                    value={formData.hive_components[compKey] || 0}
                    onChange={(e) => updateHiveComponents(compKey, e.target.value)}
                    className="h-10 mt-1" />
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  // Get available frame/foundation types for selected hive type
  const getAvailableFrameTypes = () => {
    const cfg = adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
    return cfg?.frame_types || ['Plastic', 'Wooden', 'Specialty', 'Flow Frames'];
  };

  const getAvailableFoundationTypes = () => {
    const cfg = adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
    return cfg?.foundation_types || ['Wax', 'Plastic', 'Waxed Plastic', 'Strips', 'Foundationless'];
  };

  // Get the current hive type configuration
  const getCurrentHiveTypeConfig = () => {
    return adminHiveTypes.find(ht => {
      const htKey = ht.name?.toLowerCase().replace(/\s+/g, '_');
      return htKey === hiveType || ht.name === hiveType || ht.name?.toLowerCase() === hiveType;
    }) || userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
  };

  // Check if hive type requires boxes
  const hiveTypeRequiresBoxes = () => {
    const config = getCurrentHiveTypeConfig();
    // Custom types with box_types defined should show box config
    return config?.requires_boxes === true || (config?.box_types?.length > 0);
  };

  // Get available box types for the selected hive type
  const getAvailableBoxTypes = () => {
    const config = getCurrentHiveTypeConfig();
    return config?.box_types || [];
  };

  // Update box configuration
  const updateBoxConfig = (boxTypeName, updates) => {
    setBoxConfig(prev => ({
      ...prev,
      [boxTypeName]: { ...(prev[boxTypeName] || {}), ...updates }
    }));
  };

  // Handle frame type selection
  const handleFrameTypeToggle = (frameType) => {
    if (selectedFrameTypes.includes(frameType)) {
      setSelectedFrameTypes(selectedFrameTypes.filter(f => f !== frameType));
      const newQuantities = { ...frameQuantities };
      delete newQuantities[frameType];
      setFrameQuantities(newQuantities);
    } else {
      setSelectedFrameTypes([...selectedFrameTypes, frameType]);
      setFrameQuantities({ ...frameQuantities, [frameType]: 0 });
    }
    setAllFramesSelected(false);
  };

  // Handle foundation type selection
  const handleFoundationTypeToggle = (foundationType) => {
    if (selectedFoundationTypes.includes(foundationType)) {
      setSelectedFoundationTypes(selectedFoundationTypes.filter(f => f !== foundationType));
      const newQuantities = { ...foundationQuantities };
      delete newQuantities[foundationType];
      setFoundationQuantities(newQuantities);
    } else {
      setSelectedFoundationTypes([...selectedFoundationTypes, foundationType]);
      setFoundationQuantities({ ...foundationQuantities, [foundationType]: 0 });
    }
    setAllFoundationsSelected(false);
  };

  // Handle "All" selection for frames
  const handleAllFramesToggle = () => {
    const availableFrames = getAvailableFrameTypes();
    if (allFramesSelected) {
      setSelectedFrameTypes([]);
      setFrameQuantities({});
      setAllFramesSelected(false);
    } else {
      setSelectedFrameTypes(availableFrames);
      const newQuantities = {};
      availableFrames.forEach(f => newQuantities[f] = frameQuantities[f] || 0);
      setFrameQuantities(newQuantities);
      setAllFramesSelected(true);
    }
  };

  // Handle "All" selection for foundations
  const handleAllFoundationsToggle = () => {
    const availableFoundations = getAvailableFoundationTypes();
    if (allFoundationsSelected) {
      setSelectedFoundationTypes([]);
      setFoundationQuantities({});
      setAllFoundationsSelected(false);
    } else {
      setSelectedFoundationTypes(availableFoundations);
      const newQuantities = {};
      availableFoundations.forEach(f => newQuantities[f] = foundationQuantities[f] || 0);
      setFoundationQuantities(newQuantities);
      setAllFoundationsSelected(true);
    }
  };

  const [formData, setFormData] = useState({
    hive_number: '',
    nickname: '',
    yard: '',
    location: '',
    notes: '',
    hive_image: '',
    status: 'active',
    inactive_reason: '',
    is_split: false,
    split_from_hive: '',
    split_type: '',
    split_type_other: '',
    bee_variety: '',
    origin_type: '',
    origin_seller: '',
    swarm_source: '',
    swarm_trap_id: '',
    frame_types: [],
    frame_quantities: {},
    foundation_types: [],
    foundation_quantities: {},
    hive_config: {
      // Set default values to prevent undefined comparisons
      langstroth_deep_brood_size: '10',
      langstroth_deep_brood_qty: 0,
      langstroth_deep_super_size: '10',
      langstroth_deep_super_qty: 0,
      langstroth_ideal_super_size: '10',
      langstroth_ideal_super_qty: 0,
    },
    hive_components: {
      hive_entrance_count: 1,
    },
  });

  // Derive a live stack config for PricingSection reactivity
  const liveStackConfig = useMemo(() => {
    const config = { ...formData.hive_config };
    Object.entries(boxConfig).forEach(([boxName, boxData]) => {
      const formKey = boxName.toLowerCase().replace(/\s+/g, '_');
      config[formKey] = boxData.qty || 0;
      config[`${formKey}_qty`] = boxData.qty || 0;
      config[`${formKey}_size`] = boxData.size || '';
    });
    return config;
  }, [formData.hive_config, boxConfig]);

  // Initialize form data from hive prop
  useEffect(() => {
    if (hive) {
      const stack = hive.hive_stack || {};
      const boxConfig = stack.box_config || {};
      
      // Helper to get qty from box_config
      const getBoxQty = (boxName) => boxConfig[boxName]?.qty || 0;
      const getBoxSize = (boxName, defaultSize) => boxConfig[boxName]?.size || defaultSize;
      
      // Determine hive type from saved data
      // Handle various formats: "Langstroth", "langstroth", "Flow Hive", "flow_hive", etc.
      let rawType = stack.hive_type || '';
      let detectedType = rawType;
      
      // Check if rawType directly matches an admin hive type name (case-insensitive)
      // This handles bulk-created hives that store "Langstroth" format
      const matchingAdminType = adminHiveTypes.find(
        ht => ht.name.toLowerCase() === rawType.toLowerCase()
      );
      if (matchingAdminType) {
        detectedType = matchingAdminType.name; // Use exact admin name
      } else {
        // Fall back to normalized format for legacy data
        detectedType = rawType.toLowerCase().replace(/\s+/g, '_');
      }
      
      // If still no detected type, try to infer from existing data
      if (!detectedType) {
        // Try to detect from existing data (check both old and new formats)
        const hasLangstroth = getBoxQty('Deep Brood') > 0 || getBoxQty('Deep Super') > 0 || getBoxQty('Ideal Super') > 0 ||
                             stack.deep_brood_qty > 0 || stack.deep_super_qty > 0 || stack.ideal_super_qty > 0;
        const hasFlowHive = getBoxQty('Flow Hive Brood') > 0 || getBoxQty('Flow Hive Super') > 0 ||
                          stack.flow_hive_deep_brood_qty > 0 || stack.flow_hive_super_size;
        const hasNational = getBoxQty('National Deep') > 0 || getBoxQty('National Shallow') > 0 ||
                          stack.national_deep_qty > 0 || stack.national_shallow_qty > 0;
        
        // Helper to find admin hive type by common variations
        const findAdminType = (searchTerms) => {
          return adminHiveTypes.find(ht => 
            searchTerms.some(term => ht.name.toLowerCase().includes(term.toLowerCase()))
          )?.name;
        };
        
        if (hasLangstroth) {
          detectedType = findAdminType(['langstroth']) || 'langstroth';
        } else if (stack.top_hive_frames) {
          detectedType = findAdminType(['top bar']) || 'top_bar';
        } else if (stack.horizontal_hive_frames) {
          detectedType = findAdminType(['horizontal']) || 'horizontal';
        } else if (stack.layens_hive_frames) {
          detectedType = findAdminType(['layens']) || 'layens';
        } else if (stack.nuc_frames) {
          detectedType = findAdminType(['nuc']) || 'nuc';
        } else if (hasFlowHive) {
          detectedType = findAdminType(['flow']) || 'flow_hive';
        } else if (hasNational) {
          detectedType = findAdminType(['national']) || 'national';
        } else if (stack.keepers_type) {
          detectedType = findAdminType(['keepers']) || 'keepers';
        } else if (stack.warre_boxes > 0) {
          detectedType = findAdminType(['warre']) || 'warre';
        } else if (stack.queen_mating_frames) {
          detectedType = findAdminType(['queen mating']) || 'queen_mating';
        }
      }
      
      // Normalize hive type to lowercase for consistency with switch statements
      const normalizedType = detectedType ? detectedType.toLowerCase().replace(/\s+/g, '_') : '';
      
      // Check if this is a user custom hive type (match by name)
      const matchingUserType = userHiveTypes.find(
        ut => ut.name.toLowerCase().replace(/\s+/g, '_') === normalizedType ||
              ut.name.toLowerCase() === rawType.toLowerCase() ||
              ut.name === rawType
      );
      if (matchingUserType) {
        setHiveType(`custom_${matchingUserType.id}`);
      } else {
        setHiveType(normalizedType);
      }
      setIsSplit(hive.is_split || false);
      setSplitType(hive.split_type || '');
      setSplitTypeOther(hive.split_type_other || '');
      
      // Load frame and foundation selections
      setSelectedFrameTypes(hive.frame_types || []);
      setFrameQuantities(hive.frame_quantities || {});
      setSelectedFoundationTypes(hive.foundation_types || []);
      setFoundationQuantities(hive.foundation_quantities || {});
      
      // Load box configuration (new dynamic box system)
      // If the admin hive type now uses requires_boxes but hive was saved with legacy flat fields,
      // convert legacy data to dynamic box_config format
      let loadedBoxConfig = {};
      const topLevelBoxConfig = hive.box_config || {};
      const stackBoxConfig = stack.box_config || {};
      const topHasData = Object.keys(topLevelBoxConfig).length > 0 && Object.values(topLevelBoxConfig).some(v => v && v.qty > 0);
      const stackHasData = Object.keys(stackBoxConfig).length > 0 && Object.values(stackBoxConfig).some(v => v && v.qty > 0);
      loadedBoxConfig = topHasData ? topLevelBoxConfig : stackHasData ? stackBoxConfig : {};
      const hasBoxData = Object.keys(loadedBoxConfig).length > 0 && Object.values(loadedBoxConfig).some(v => v && v.qty > 0);
      
      if (!hasBoxData) {
        const adminType = adminHiveTypes.find(ht => ht.name.toLowerCase().replace(/\s+/g, '_') === normalizedType);
        if (adminType?.requires_boxes) {
          const legacyBoxConfig = {};
          if (normalizedType === 'langstroth') {
            legacyBoxConfig['Deep Brood'] = { qty: stack.deep_brood_qty || 0, size: stack.deep_brood_size || '10' };
            legacyBoxConfig['Deep Super'] = { qty: stack.deep_super_qty || 0, size: stack.deep_super_size || '10' };
            legacyBoxConfig['Ideal Super'] = { qty: stack.ideal_super_qty || 0, size: stack.ideal_super_size || '10' };
          } else if (normalizedType === 'flow_hive') {
            legacyBoxConfig['Flow Hive Brood'] = { qty: stack.flow_hive_deep_brood_qty || 0, size: stack.flow_hive_deep_brood_size || '10' };
            legacyBoxConfig['Flow Hive Super'] = { qty: 0, size: stack.flow_hive_super_size || '6' };
          } else if (normalizedType === 'national') {
            legacyBoxConfig['National Deep'] = { qty: stack.national_deep_qty || 0, size: stack.national_deep_size || '11' };
            legacyBoxConfig['National Shallow'] = { qty: stack.national_shallow_qty || 0, size: stack.national_shallow_size || '10' };
          } else if (normalizedType === 'keepers') {
            legacyBoxConfig['Keepers Langstroth Deep'] = { qty: stack.keepers_langstroth_deep_qty || 0, size: '10' };
            legacyBoxConfig['Keepers Medium NUC'] = { qty: stack.keepers_medium_nuc_qty || 0, size: '5' };
            legacyBoxConfig['Keepers Super Deep'] = { qty: stack.keepers_super_deep_qty || 0, size: '8' };
          } else if (normalizedType === 'warre') {
            legacyBoxConfig['Warre'] = { qty: stack.warre_boxes || 0, size: '8' };
          }
          if (Object.values(legacyBoxConfig).some(v => v.qty > 0)) {
            loadedBoxConfig = legacyBoxConfig;
          }
        }
      }
      setBoxConfig(loadedBoxConfig);
      
      setFormData({
        hive_number: hive.hive_number,
        nickname: hive.nickname || '',
        yard: hive.yard || '',
        location: hive.location || '',
        notes: hive.notes || '',
        hive_image: hive.hive_image || '',
        status: hive.status || 'active',
        inactive_reason: hive.inactive_reason || '',
        is_split: hive.is_split || false,
        split_from_hive: hive.split_from_hive || '',
        split_type: hive.split_type || '',
        split_type_other: hive.split_type_other || '',
        bee_variety: hive.bee_variety || '',
        origin_type: hive.origin_type || '',
        origin_seller: hive.origin_seller || '',
        swarm_source: hive.swarm_source || '',
        swarm_trap_id: hive.swarm_trap_id || '',
        frame_types: hive.frame_types || [],
        frame_quantities: hive.frame_quantities || {},
        foundation_types: hive.foundation_types || [],
        foundation_quantities: hive.foundation_quantities || {},
        hive_config: {
          // Langstroth - support both old format (stack.deep_brood_qty) and new format (box_config)
          langstroth_deep_brood_size: getBoxSize('Deep Brood', '10') || stack.deep_brood_size || '10',
          langstroth_deep_brood_qty: getBoxQty('Deep Brood') || stack.deep_brood_qty || 0,
          langstroth_deep_super_size: getBoxSize('Deep Super', '10') || stack.deep_super_size || '10',
          langstroth_deep_super_qty: getBoxQty('Deep Super') || stack.deep_super_qty || 0,
          langstroth_ideal_super_size: getBoxSize('Ideal Super', '10') || stack.ideal_super_size || '10',
          langstroth_ideal_super_qty: getBoxQty('Ideal Super') || stack.ideal_super_qty || 0,
          // Top Bar
          top_bar_frames: stack.top_hive_frames || '',
          // Horizontal
          horizontal_frames: stack.horizontal_hive_frames || '',
          horizontal_entrances: stack.horizontal_hive_entrances || '',
          horizontal_colonies: stack.horizontal_hive_colonies || '',
          // Layens
          layens_frames: stack.layens_hive_frames || '',
          // NUC
          nuc_frames: stack.nuc_frames || '',
          // Flow Hive
          flow_deep_brood_size: getBoxSize('Flow Hive Brood', '10') || stack.flow_hive_deep_brood_size || '10',
          flow_deep_brood_qty: getBoxQty('Flow Hive Brood') || stack.flow_hive_deep_brood_qty || 0,
          flow_super_size: getBoxSize('Flow Hive Super', '6') || stack.flow_hive_super_size || '6',
          // National
          national_variant: stack.national_variant || '1',
          national_deep_size: getBoxSize('National Deep', '11') || stack.national_deep_size || '11',
          national_deep_qty: getBoxQty('National Deep') || stack.national_deep_qty || 0,
          national_shallow_size: getBoxSize('National Shallow', '10') || stack.national_shallow_size || '10',
          national_shallow_qty: getBoxQty('National Shallow') || stack.national_shallow_qty || 0,
          // Keepers
          keepers_type: stack.keepers_type || 'one_queen',
          keepers_langstroth_deep_qty: getBoxQty('Keepers Langstroth Deep') || stack.keepers_langstroth_deep_qty || 0,
          keepers_medium_nuc_qty: getBoxQty('Keepers Medium NUC') || stack.keepers_medium_nuc_qty || 0,
          keepers_super_deep_qty: getBoxQty('Keepers Super Deep') || stack.keepers_super_deep_qty || 0,
          // Warre
          warre_boxes: getBoxQty('Warre') || stack.warre_boxes || 0,
          // Queen Mating
          queen_mating_frames: stack.queen_mating_frames || '',
          // Other
          other_description: stack.other_description || stack.other_hive || '',
        },
        hive_components: {
          // Load ALL saved component values dynamically
          ...(hive.hive_components || {}),
          // Ensure entrance count defaults to 1
          hive_entrance_count: hive.hive_components?.hive_entrance_count || 1,
        },
      });
      
      // Load pricing data - only apply defaults for components that exist in the hive
      const existingPricing = hive.hive_pricing || {};
      setHivePricing(existingPricing);
      setOriginPrice(hive.origin_price || 0);
      
      // Load frame and foundation types
      if (hive.frame_types && Array.isArray(hive.frame_types)) {
        setSelectedFrameTypes(hive.frame_types);
      }
      if (hive.foundation_types && Array.isArray(hive.foundation_types)) {
        setSelectedFoundationTypes(hive.foundation_types);
      }
      
      // Load frame and foundation quantities from box_config
      const boxCfg = hive.hive_stack?.box_config || hive.box_config || {};
      const frameQtys = {};
      const foundationQtys = {};
      Object.values(boxCfg).forEach(box => {
        if (box.qty > 0) {
          // Get size for number of frames
          const frameCount = parseInt(box.size) || 10;
          if (box.frame_type) {
            frameQtys[box.frame_type] = (frameQtys[box.frame_type] || 0) + (box.qty * frameCount);
          }
          if (box.foundation_type) {
            foundationQtys[box.foundation_type] = (foundationQtys[box.foundation_type] || 0) + (box.qty * frameCount);
          }
        }
      });
      if (Object.keys(frameQtys).length > 0) {
        setFrameQuantities(frameQtys);
      }
      if (Object.keys(foundationQtys).length > 0) {
        setFoundationQuantities(foundationQtys);
      }
    }
  }, [hive, adminHiveTypes, userHiveTypes]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload);
      setFormData({ ...formData, hive_image: response.data.file_id });
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const updateConfig = (key, value) => {
    setFormData({
      ...formData,
      hive_config: { ...formData.hive_config, [key]: value }
    });
  };

  const updateHiveComponents = (key, value) => {
    setFormData({
      ...formData,
      hive_components: { ...formData.hive_components, [key]: parseInt(value) || 0 }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Build hive_stack based on selected hive type - only include relevant fields
    // Resolve custom type ID to friendly name for display
    let resolvedHiveType = hiveType;
    if (hiveType?.startsWith('custom_')) {
      const userMatch = userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
      if (userMatch) resolvedHiveType = userMatch.name;
    }
    
    const hive_stack = {
      hive_type: resolvedHiveType,
    };
    
    // Check if this hive type uses dynamic box configuration
    const hiveTypeConfig = getCurrentHiveTypeConfig();
    const hasBoxTypes = hiveTypeConfig?.requires_boxes || (hiveTypeConfig?.box_types?.length > 0);
    if (hasBoxTypes) {
      // Use the dynamic box configuration
      hive_stack.box_config = boxConfig;
    } else {
      // Only add fields relevant to the selected hive type (legacy support)
      switch (hiveType) {
        case 'langstroth':
          hive_stack.deep_brood_size = formData.hive_config.langstroth_deep_brood_size || '10';
          hive_stack.deep_brood_qty = formData.hive_config.langstroth_deep_brood_qty || 0;
          hive_stack.deep_super_size = formData.hive_config.langstroth_deep_super_size || '10';
          hive_stack.deep_super_qty = formData.hive_config.langstroth_deep_super_qty || 0;
          hive_stack.ideal_super_size = formData.hive_config.langstroth_ideal_super_size || '10';
          hive_stack.ideal_super_qty = formData.hive_config.langstroth_ideal_super_qty || 0;
          break;
        case 'top_bar':
          hive_stack.top_hive_frames = formData.hive_config.top_bar_frames || '';
          break;
        case 'horizontal':
          hive_stack.horizontal_hive_frames = formData.hive_config.horizontal_frames || '';
          hive_stack.horizontal_hive_entrances = formData.hive_config.horizontal_entrances || '';
          hive_stack.horizontal_hive_colonies = formData.hive_config.horizontal_colonies || '';
          break;
        case 'layens':
          hive_stack.layens_hive_frames = formData.hive_config.layens_frames || '';
          break;
        case 'nuc':
          hive_stack.nuc_frames = formData.hive_config.nuc_frames || '';
          break;
        case 'flow_hive':
          hive_stack.flow_hive_deep_brood_size = formData.hive_config.flow_deep_brood_size || '10';
          hive_stack.flow_hive_deep_brood_qty = formData.hive_config.flow_deep_brood_qty || 0;
          hive_stack.flow_hive_super_size = formData.hive_config.flow_super_size || '6';
          break;
        case 'national':
          hive_stack.national_variant = formData.hive_config.national_variant || '1';
          hive_stack.national_deep_size = formData.hive_config.national_deep_size || '11';
          hive_stack.national_deep_qty = formData.hive_config.national_deep_qty || 0;
          hive_stack.national_shallow_size = formData.hive_config.national_shallow_size || '10';
          hive_stack.national_shallow_qty = formData.hive_config.national_shallow_qty || 0;
          break;
        case 'keepers':
          hive_stack.keepers_type = formData.hive_config.keepers_type || 'one_queen';
          hive_stack.keepers_langstroth_deep_qty = formData.hive_config.keepers_langstroth_deep_qty || 0;
          hive_stack.keepers_medium_nuc_qty = formData.hive_config.keepers_medium_nuc_qty || 0;
          hive_stack.keepers_super_deep_qty = formData.hive_config.keepers_super_deep_qty || 0;
          break;
        case 'warre':
          hive_stack.warre_boxes = formData.hive_config.warre_boxes || 0;
          break;
        case 'queen_mating':
          hive_stack.queen_mating_frames = formData.hive_config.queen_mating_frames || '';
          break;
        case 'other':
          hive_stack.other_description = formData.hive_config.other_description || '';
          break;
        default:
          break;
      }
    }
    
    onUpdate({
      ...formData,
      hive_stack: hive_stack,
      box_config: boxConfig, // Also save at top level for easier access
      hive_pricing: hivePricing,
      origin_price: originPrice,
      frame_types: selectedFrameTypes,
      frame_quantities: frameQuantities,
      foundation_types: selectedFoundationTypes,
      foundation_quantities: foundationQuantities,
    });
  };

  // Render dynamic box configuration for hive types with requires_boxes = true
  const renderDynamicBoxConfig = () => {
    const availableBoxTypes = getAvailableBoxTypes();
    const frameTypes = getAvailableFrameTypes();
    const foundationTypes = getAvailableFoundationTypes();
    
    if (!availableBoxTypes || availableBoxTypes.length === 0) {
      return (
        <div className="text-sm text-gray-500 italic">
          No box types configured for this hive type. Configure them in Admin Configuration.
        </div>
      );
    }
    
    return (
      <div className="space-y-4">
        {availableBoxTypes.map((boxType, index) => {
          // Check both state and hive prop for box configuration
          const hiveBoxConfig = hive?.hive_stack?.box_config || hive?.box_config || {};
          const config = boxConfig[boxType.name] || hiveBoxConfig[boxType.name] || { qty: 0, size: boxType.sizes?.[0] || '10', frame_type: '', foundation_type: '' };
          const sizes = boxType.sizes || ['8', '10'];
          const maxQty = boxType.max_qty || 4;
          
          return (
            <div key={index} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3">
              <div className="font-medium text-amber-800 text-sm mb-2">{boxType.name}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-xs text-stone-500 mb-1">Frame Size</div>
                  <div className="flex gap-1">
                    {sizes.map((s) => (
                      <Toggle
                        key={s}
                        pressed={config.size === s}
                        onPressedChange={() => updateBoxConfig(boxType.name, { size: s })}
                        className="h-9 flex-1 text-sm data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                      >
                        {s}F
                      </Toggle>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-stone-500 mb-1"># of Boxes</div>
                  <div className="flex gap-1">
                    {Array.from({ length: maxQty + 1 }, (_, i) => i).map((q) => (
                      <Toggle
                        key={q}
                        pressed={config.qty === q}
                        onPressedChange={() => updateBoxConfig(boxType.name, { qty: q })}
                        className="h-9 flex-1 text-sm data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                      >
                        {q}
                      </Toggle>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render hive-specific configuration options
  const renderHiveConfig = () => {
    // For custom hive types or admin types with box_types, render dynamic box config
    const hiveTypeConfig = getCurrentHiveTypeConfig();
    const boxTypes = hiveTypeConfig?.box_types || [];
    if (boxTypes.length > 0) {
      return (
        <HiveTypeConfig
          hiveType={hiveType}
          config={formData.hive_config}
          updateConfig={updateConfig}
          customBoxTypes={boxTypes}
          boxConfig={boxConfig}
          onBoxConfigChange={(name, cfg) => updateBoxConfig(name, cfg)}
        />
      );
    }
    // Standard hive types use the built-in switch-case rendering
    return (
      <HiveTypeConfig hiveType={hiveType} config={formData.hive_config} updateConfig={updateConfig} />
    );
  };


  if (!hive) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-xl sm:text-2xl font-bold text-stone-800 font-heading">
            Edit Hive {hive.hive_number}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4 min-w-0 overflow-hidden">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-stone-700 font-medium">Hive Number (ID)</Label>
              <Input
                type="number"
                value={formData.hive_number}
                onChange={(e) => setFormData({ ...formData, hive_number: e.target.value })}
                className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1"
              />
            </div>
            <div>
              <Label className="text-stone-700 font-medium">Hive Nickname <span className="text-stone-400 text-sm">(optional)</span></Label>
              <Input
                type="text"
                placeholder="e.g., Queen Bee, Sunny"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                className="h-12 bg-stone-50 border-stone-200 rounded-xl mt-1"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-stone-700 font-medium">Yard</Label>
            {/* Show warning if current yard doesn't exist in yards list */}
            {formData.yard && yards && !yards.some(y => y.name === formData.yard) && (
              <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded mt-1 mb-1">
                ⚠️ Previous yard &quot;{formData.yard}&quot; was deleted. Please select a new yard.
              </div>
            )}
            {!formData.yard && (
              <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded mt-1 mb-1">
                ⚠️ No apiary assigned — please select one.
              </div>
            )}
            <Select
              value={yards?.some(y => y.name === formData.yard) ? formData.yard : ""}
              onValueChange={(value) => setFormData({ ...formData, yard: value })}
            >
              <SelectTrigger className={`h-12 bg-stone-50 rounded-xl mt-1 ${!formData.yard ? 'border-red-400 border-2' : 'border-stone-200'}`}>
                <SelectValue placeholder="Select apiary" />
              </SelectTrigger>
              <SelectContent>
                {yards?.map((yard) => (
                  <SelectItem key={yard.id} value={yard.name}>{yard.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bee Variety */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              🐝 Bee Variety
            </Label>
            <Select
              value={formData.bee_variety}
              onValueChange={(value) => setFormData({ ...formData, bee_variety: value })}
            >
              <SelectTrigger className="h-12">
                <SelectValue placeholder="Select bee variety" />
              </SelectTrigger>
              <SelectContent>
                {adminBeeVarieties.map((variety) => (
                  <SelectItem key={variety.id} value={variety.name}>
                    {variety.name}
                    {variety.temperament && <span className="text-stone-400 ml-2">({variety.temperament})</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Origin Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              📦 Origin (Optional)
            </Label>
            <div className="space-y-3">
              <div>
                <Label className="text-stone-600 text-sm">Received as</Label>
                <Select
                  value={formData.origin_type || ''}
                  onValueChange={(value) => {
                    const originConfig = adminOriginTypes.find(o => o.name === value);
                    setFormData({ 
                      ...formData, 
                      origin_type: value, 
                      swarm_source: '', 
                      swarm_trap_id: '',
                      origin_seller: originConfig?.requires_seller ? formData.origin_seller : ''
                    });
                  }}
                >
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select origin type" />
                  </SelectTrigger>
                  <SelectContent>
                    {adminOriginTypes.map((type) => (
                      <SelectItem key={type.id} value={type.name}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Show seller field if origin type requires it */}
              {formData.origin_type && (() => {
                const originConfig = adminOriginTypes.find(o => o.name === formData.origin_type);
                return originConfig?.requires_seller;
              })() && (
                <div>
                  <Label className="text-stone-600 text-sm">From (Seller/Source)</Label>
                  <Input
                    value={formData.origin_seller || ''}
                    onChange={(e) => setFormData({ ...formData, origin_seller: e.target.value })}
                    placeholder="Seller or source name"
                    className="h-12 mt-1"
                  />
                </div>
              )}
              
              {/* Show price field if origin type requires it */}
              {formData.origin_type && (() => {
                const originConfig = adminOriginTypes.find(o => o.name === formData.origin_type);
                return originConfig?.requires_price;
              })() && (
                <div>
                  <Label className="text-stone-600 text-sm">Purchase Price ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.origin_price || ''}
                    onChange={(e) => setFormData({ ...formData, origin_price: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                    className="h-12 mt-1"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Is this a Split? */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium text-base mb-3 block">
              ✂️ Is this hive from a Split?
            </Label>
            <div className="flex gap-3">
              <Toggle
                pressed={isSplit}
                onPressedChange={(pressed) => {
                  setIsSplit(pressed);
                  setFormData({ ...formData, is_split: pressed });
                  if (!pressed) {
                    setSplitType('');
                    setSplitTypeOther('');
                    setFormData({ ...formData, is_split: false, split_from_hive: '', split_type: '', split_type_other: '' });
                  }
                }}
                className="h-12 px-6 data-[state=on]:bg-emerald-600 data-[state=on]:text-white font-bold"
              >
                Yes, from Split
              </Toggle>
              <Toggle
                pressed={!isSplit}
                onPressedChange={() => {
                  setIsSplit(false);
                  setSplitType('');
                  setSplitTypeOther('');
                  setFormData({ ...formData, is_split: false, split_from_hive: '', split_type: '', split_type_other: '' });
                }}
                className="h-12 px-6 data-[state=on]:bg-stone-600 data-[state=on]:text-white font-bold"
              >
                No
              </Toggle>
            </div>
            {isSplit && (
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-stone-600 text-sm">Split from which hive?</Label>
                  <Select
                    value={formData.split_from_hive}
                    onValueChange={(value) => setFormData({ ...formData, split_from_hive: value })}
                  >
                    <SelectTrigger className="h-12 mt-1">
                      <SelectValue placeholder="Select parent hive">
                        {formData.split_from_hive && (
                          (() => {
                            const parentHive = allHives?.find(h => h.id === formData.split_from_hive);
                            return parentHive ? `Hive ${parentHive.hive_number} (${parentHive.yard || 'No yard'})` : formData.split_from_hive;
                          })()
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {allHives?.filter(h => h.id !== hive?.id).map((h) => (
                        <SelectItem key={h.id} value={h.id}>
                          Hive {h.hive_number} ({h.yard || 'No yard'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-stone-600 text-sm mb-2 block">Split Type</Label>
                  <div className="grid grid-cols-3 gap-2">
                    <Toggle
                      pressed={splitType === 'walk-away'}
                      onPressedChange={() => {
                        setSplitType('walk-away');
                        setSplitTypeOther('');
                        setFormData({ ...formData, split_type: 'walk-away', split_type_other: '' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Walk-away Split
                    </Toggle>
                    <Toggle
                      pressed={splitType === 'queen-right'}
                      onPressedChange={() => {
                        setSplitType('queen-right');
                        setSplitTypeOther('');
                        setFormData({ ...formData, split_type: 'queen-right', split_type_other: '' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Queen Right
                    </Toggle>
                    <Toggle
                      pressed={splitType === 'other'}
                      onPressedChange={() => {
                        setSplitType('other');
                        setFormData({ ...formData, split_type: 'other' });
                      }}
                      className="h-12 data-[state=on]:bg-emerald-600 data-[state=on]:text-white"
                    >
                      Other
                    </Toggle>
                  </div>
                  {splitType === 'other' && (
                    <Input
                      className="mt-2 h-12"
                      placeholder="Describe the split type..."
                      value={splitTypeOther}
                      onChange={(e) => {
                        setSplitTypeOther(e.target.value);
                        setFormData({ ...formData, split_type_other: e.target.value });
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Hive Status */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-4">
            <Label className="text-stone-700 font-medium mb-3 block">Hive Status</Label>
            <div className="flex gap-3">
              <Toggle
                pressed={formData.status === 'active'}
                onPressedChange={() => setFormData({ ...formData, status: 'active', inactive_reason: '' })}
                className="h-12 px-6 data-[state=on]:bg-green-600 data-[state=on]:text-white font-bold"
              >
                🐝 Active
              </Toggle>
              <Toggle
                pressed={formData.status === 'inactive'}
                onPressedChange={() => setFormData({ ...formData, status: 'inactive' })}
                className="h-12 px-6 data-[state=on]:bg-red-600 data-[state=on]:text-white font-bold"
              >
                ⚠️ Inactive
              </Toggle>
            </div>
            {formData.status === 'inactive' && (
              <div className="mt-3">
                <Label className="text-stone-600 text-sm">Reason for Inactive Status</Label>
                <Select
                  value={formData.inactive_reason}
                  onValueChange={(value) => setFormData({ ...formData, inactive_reason: value })}
                >
                  <SelectTrigger className="h-12 mt-1">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Dead Out', 'Absconded', 'Combined', 'Sold', 'Stolen',
                      'Varroa Mites', 'Small Hive Beetle (SHB)', 'Wax Moths', 'Starvation',
                      'Queenless', 'Pesticide Exposure', 'Robbing', 'Disease (AFB/EFB)',
                      'Nosema', 'Cold / Exposure', 'Other'].map((reason) => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Hive Type Dropdown */}
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
            <Label className="text-stone-800 font-bold text-base mb-3 block">
              🏠 Hive Type
            </Label>
            <Select
              value={hiveType}
              onValueChange={(value) => {
                // Custom types use custom_{id} format - preserve as-is
                if (value && value.startsWith('custom_')) {
                  setHiveType(value);
                } else {
                  const normalizedValue = value ? value.toLowerCase().replace(/\s+/g, '_') : '';
                  setHiveType(normalizedValue);
                }
                // Reset frame and foundation selections when hive type changes
                setSelectedFrameTypes([]);
                setSelectedFoundationTypes([]);
                setFrameQuantities({});
                setFoundationQuantities({});
                setAllFramesSelected(false);
                setAllFoundationsSelected(false);
                setBoxConfig({});
              }}
            >
              <SelectTrigger className="h-14 bg-white border-amber-300 rounded-xl text-lg font-medium">
                <SelectValue placeholder="Select hive type...">
                  {/* Display the proper name even though we store lowercase */}
                  {(() => {
                    if (!hiveType) return 'Select type';
                    const adminMatch = adminHiveTypes.find(ht => ht.name?.toLowerCase().replace(/\s+/g, '_') === hiveType);
                    if (adminMatch) return adminMatch.name;
                    const userMatch = userHiveTypes.find(ht => `custom_${ht.id}` === hiveType);
                    if (userMatch) return userMatch.name;
                    return hiveType;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {adminHiveTypes.length > 0 ? (
                  adminHiveTypes.map((type) => (
                    <SelectItem key={type.id} value={type.name.toLowerCase().replace(/\s+/g, '_')} className="text-base py-3">
                      {type.name}
                    </SelectItem>
                  ))
                ) : (
                  HIVE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value} className="text-base py-3">
                      {type.label}
                    </SelectItem>
                  ))
                )}
                {/* User custom hive types */}
                {userHiveTypes.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-xs text-stone-400 border-t mt-1 pt-1">My Custom Types</div>
                    {userHiveTypes.map((type) => (
                      <SelectItem key={type.id} value={`custom_${type.id}`} className="text-base py-3">
                        {type.name}
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
            
            {/* Dynamic Hive Configuration */}
            {renderHiveConfig()}
          </div>

          {/* Frame Types Selection */}
          {hiveType && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <Label className="text-stone-800 font-bold text-base mb-3 block">
                🖼️ Frame Types
              </Label>
              <p className="text-sm text-gray-600 mb-3">Select frame types used in this hive</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Toggle
                  pressed={allFramesSelected}
                  onPressedChange={handleAllFramesToggle}
                  className="h-10 px-4 data-[state=on]:bg-blue-600 data-[state=on]:text-white border border-blue-300"
                >
                  All
                </Toggle>
                {getAvailableFrameTypes().map((frameType) => (
                  <Toggle
                    key={frameType}
                    pressed={selectedFrameTypes.includes(frameType)}
                    onPressedChange={() => handleFrameTypeToggle(frameType)}
                    className="h-10 px-4 data-[state=on]:bg-blue-600 data-[state=on]:text-white border border-blue-300"
                  >
                    {frameType}
                  </Toggle>
                ))}
              </div>
              
              {/* Frame Quantities */}
              {selectedFrameTypes.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-blue-200">
                  {selectedFrameTypes.map((frameType) => (
                    <div key={frameType}>
                      <Label className="text-xs text-blue-700"># of {frameType}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={frameQuantities[frameType] || 0}
                        onChange={(e) => setFrameQuantities({
                          ...frameQuantities,
                          [frameType]: parseInt(e.target.value) || 0
                        })}
                        className="h-10 mt-1 bg-white"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Foundation Types Selection */}
          {hiveType && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <Label className="text-stone-800 font-bold text-base mb-3 block">
                🧱 Foundation Types
              </Label>
              <p className="text-sm text-gray-600 mb-3">Select foundation types used in this hive</p>
              
              <div className="flex flex-wrap gap-2 mb-4">
                <Toggle
                  pressed={allFoundationsSelected}
                  onPressedChange={handleAllFoundationsToggle}
                  className="h-10 px-4 data-[state=on]:bg-purple-600 data-[state=on]:text-white border border-purple-300"
                >
                  All
                </Toggle>
                {getAvailableFoundationTypes().map((foundationType) => (
                  <Toggle
                    key={foundationType}
                    pressed={selectedFoundationTypes.includes(foundationType)}
                    onPressedChange={() => handleFoundationTypeToggle(foundationType)}
                    className="h-10 px-4 data-[state=on]:bg-purple-600 data-[state=on]:text-white border border-purple-300"
                  >
                    {foundationType}
                  </Toggle>
                ))}
              </div>
              
              {/* Foundation Quantities */}
              {selectedFoundationTypes.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t border-purple-200">
                  {selectedFoundationTypes.map((foundationType) => (
                    <div key={foundationType}>
                      <Label className="text-xs text-purple-700"># of {foundationType}</Label>
                      <Input
                        type="number"
                        min="0"
                        value={foundationQuantities[foundationType] || 0}
                        onChange={(e) => setFoundationQuantities({
                          ...foundationQuantities,
                          [foundationType]: parseInt(e.target.value) || 0
                        })}
                        className="h-10 mt-1 bg-white"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Hive Components */}
          <div className="border-t border-stone-200 pt-4">
            <h3 className="text-lg font-bold text-stone-800 flex items-center gap-2 mb-4">
              <Package className="h-5 w-5 text-amber-600" /> Hive Components
            </h3>
            
            <div className="space-y-3">
              {renderDynamicComponentSections()}
            </div>

            {/* Suggest New Component */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-4">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">💡 Can&apos;t find what you need?</h4>
              <p className="text-xs text-amber-700 mb-2">Suggest a new component and your admin will review it.</p>
              <div className="flex gap-2">
                <Input
                  placeholder="Component name..."
                  value={suggestionName}
                  onChange={(e) => setSuggestionName(e.target.value)}
                  className="flex-1 h-9 bg-white"
                />
                <Button 
                  type="button"
                  onClick={handleSuggestComponent}
                  disabled={!suggestionName.trim()}
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 h-9"
                >
                  Suggest
                </Button>
              </div>
            </div>
          </div>

          {/* Hive Image */}
          <div>
            <Label className="text-stone-700 font-medium mb-3 block">🖼️ Hive Background Image</Label>
            <div className="flex items-center gap-4">
              {formData.hive_image ? (
                <div className="relative">
                  <img
                    src={`${API}/uploads/${formData.hive_image}`}
                    alt="Hive Background"
                    className="w-24 h-16 rounded-lg object-cover border-2 border-stone-200"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, hive_image: '' })}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-16 rounded-lg bg-stone-200 flex items-center justify-center border-2 border-stone-300">
                  <span className="text-stone-500 text-xs">No Image</span>
                </div>
              )}
              <div className="flex gap-2">
                <input type="file" accept="image/*" capture="environment" onChange={handleImageUpload} className="hidden" id="edit-hive-camera" disabled={uploadingImage} />
                <Button type="button" variant="secondary" disabled={uploadingImage} onClick={() => document.getElementById('edit-hive-camera').click()} className="h-10 px-3 bg-purple-100 hover:bg-purple-200 text-purple-700">
                  <Camera className="mr-1 h-4 w-4" />Camera
                </Button>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" id="edit-hive-upload" disabled={uploadingImage} />
                <Button type="button" variant="secondary" disabled={uploadingImage} onClick={() => document.getElementById('edit-hive-upload').click()} className="h-10 px-3">
                  <Upload className="mr-1 h-4 w-4" />{uploadingImage ? '...' : 'Upload'}
                </Button>
              </div>
            </div>
          </div>

          {/* Hive Costs Section */}
          <PricingSection
            pricing={hivePricing}
            onPricingChange={setHivePricing}
            showOriginPrice={true}
            originPrice={originPrice}
            onOriginPriceChange={setOriginPrice}
            stackConfig={liveStackConfig}
            components={formData.hive_components}
            adminComponents={adminComponents}
            defaultPrices={defaultPrices}
            frameTypes={selectedFrameTypes}
            frameQuantities={frameQuantities}
            foundationTypes={selectedFoundationTypes}
            foundationQuantities={foundationQuantities}
            entityName={`Hive ${formData.hive_number}`}
          />

          {/* Notes */}
          <div>
            <Label className="text-stone-700 font-medium">Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="bg-stone-50 border-stone-200 rounded-xl mt-1 min-h-16"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 sm:gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={onClose} className="flex-1 h-11 sm:h-12 rounded-full font-semibold text-sm sm:text-base">
              Cancel
            </Button>
            <Button type="submit" data-testid="edit-hive-save-btn" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-11 sm:h-12 rounded-full font-bold text-sm sm:text-base">
              Update
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Reusable Stack Row Component

export default EditHiveDialog;
